'use strict';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiSearch } from 'react-icons/fi';
import ItemCard from '../components/pos/ItemCard';
import EditItemModal from '../components/pos/EditItemModal';
import { ItemsAPI } from '../services/api';

export default function RecurringInvoiceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [rec, setRec] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [posStateCode, setPosStateCode] = useState('');
  const [terms, setTerms] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [itemsCatalog, setItemsCatalog] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const INDIAN_STATES = [
    { code: '01', name: 'Jammu & Kashmir' }, { code: '02', name: 'Himachal Pradesh' }, { code: '03', name: 'Punjab' }, { code: '04', name: 'Chandigarh' }, { code: '05', name: 'Uttarakhand' }, { code: '06', name: 'Haryana' }, { code: '07', name: 'Delhi' }, { code: '08', name: 'Rajasthan' }, { code: '09', name: 'Uttar Pradesh' }, { code: '10', name: 'Bihar' }, { code: '11', name: 'Sikkim' }, { code: '12', name: 'Arunachal Pradesh' }, { code: '13', name: 'Nagaland' }, { code: '14', name: 'Manipur' }, { code: '15', name: 'Mizoram' }, { code: '16', name: 'Tripura' }, { code: '17', name: 'Meghalaya' }, { code: '18', name: 'Assam' }, { code: '19', name: 'West Bengal' }, { code: '20', name: 'Jharkhand' }, { code: '21', name: 'Odisha' }, { code: '22', name: 'Chhattisgarh' }, { code: '23', name: 'Madhya Pradesh' }, { code: '24', name: 'Gujarat' }, { code: '26', name: 'Daman and Diu, Dadra & Nagar Haveli' }, { code: '27', name: 'Maharashtra' }, { code: '28', name: 'Andhra Pradesh (before bifurcation)' }, { code: '36', name: 'Telangana' }, { code: '37', name: 'Andhra Pradesh (New)' }, { code: '29', name: 'Karnataka' }, { code: '30', name: 'Goa' }, { code: '31', name: 'Lakshadweep' }, { code: '32', name: 'Kerala' }, { code: '33', name: 'Tamil Nadu' }, { code: '34', name: 'Puducherry' }, { code: '35', name: 'Andaman & Nicobar Islands' }, { code: '38', name: 'Ladakh' }
  ];
  const [editSetup, setEditSetup] = useState(false);
  // Quote/Invoice-style item picker + summary UI state (does not change save payload/logic)
  const [itemSearch, setItemSearch] = useState('');
  const [itemType, setItemType] = useState('all'); // all | product | service
  const [openDisc, setOpenDisc] = useState({});
  const controlStyle = { fontSize: 12, padding: '6px 8px', height: 36, minHeight: 36 };
  const labelStyle = { fontSize: 12, color: '#374151' };
  const smallBtn = { fontSize: 12, padding: '6px 10px', borderRadius: 8, height: 32, minHeight: 32, display: 'inline-flex', alignItems: 'center' };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/api/recurring-invoices/${id}`);
        setRec(data);
        // Customer details are not included by GET /api/recurring-invoices/:id (backend does not include relation).
        // We'll fetch company customers separately for display-only summary.
        setCustomerData(null);
        setItems(Array.isArray(data?.items) ? data.items.map(line => ({
          id: line.id || null,
          itemId: line.itemId || null,
          name: line.name || '',
          description: line.description || '',
          quantity: Number(line.quantity ?? 1),
          unit: line.unit || 'NOS',
          price: Number(line.price ?? 0),
          discountAmount: Number(line.discountAmount ?? 0),
          discountPercent: Number(line.discountPercent ?? 0),
          cgstRate: Number(line.cgstRate ?? 0),
          sgstRate: Number(line.sgstRate ?? 0),
          igstRate: Number(line.igstRate ?? 0),
          cessRate: Number(line.cessRate ?? 0),
        })) : []);
        setPosStateCode(data?.posStateCode || '');
        setTerms(data?.terms || '');
        setNotes(data?.notes || '');
      } catch (e) {
        setError('Failed to load recurring invoice');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        if (!company?.id) return;
        const { data: comp } = await api.get('/api/companies/' + company.id);
        setCompanyData(comp);
        if (!posStateCode && comp?.stateCode) setPosStateCode(comp.stateCode);
        // Keep in sync with Quote/Invoice forms: gst-status is the source of truth for GSTIN + effective stateCode.
        try {
          const { data: gstStatus } = await api.get(`/api/companies/${company.id}/gst-status`);
          setCompanyData(prev => ({
            ...(prev || comp || {}),
            gstin: gstStatus?.gstin ?? (prev?.gstin ?? comp?.gstin),
            stateCode: gstStatus?.stateCode ?? (prev?.stateCode ?? comp?.stateCode)
          }));
          const effectiveState = gstStatus?.stateCode || comp?.stateCode || '';
          if (!posStateCode && effectiveState) setPosStateCode(effectiveState);
        } catch (_) {}
      } catch (_) {}
      try {
        const { data: its } = await api.get('/api/items', { params: { companyId: company?.id } });
        setItemsCatalog(Array.isArray(its) ? its : []);
      } catch (_) {}
      try {
        // Display-only: lookup the customer for the summary panel
        if (!company?.id || !rec?.customerId) return;
        const { data: custs } = await api.get('/api/companies/' + company.id + '/customers');
        const list = custs?.customers || [];
        const found = list.find(c => String(c.id) === String(rec.customerId)) || null;
        setCustomerData(found);
      } catch (_) {}
    })();
  }, [company?.id, rec?.customerId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  const addItem = () => {
    setItems(prev => [...prev, {
      id: null, itemId: null, name: '', description: '', quantity: 1, unit: 'NOS', price: 0,
      discountAmount: 0, discountPercent: 0, cgstRate: 0, sgstRate: 0, igstRate: 0, cessRate: 0
    }]);
  };
  const updateItem = (index, key, value) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, [key]: value } : it));
  };
  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const clamp = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const num = Number(val);
    if (Number.isNaN(num)) return '';
    return num < 0 ? 0 : num;
  };

  const money = (v) => {
    try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Number(v || 0)); } catch { return v; }
  };

  const refreshItemsCatalog = async () => {
    if (!company?.id) return;
    try {
      const { data: its } = await api.get('/api/items', { params: { companyId: company.id } });
      setItemsCatalog(Array.isArray(its) ? its : []);
    } catch (_) {}
  };

  const recurringQtyForItem = useMemo(() => {
    const map = new Map();
    for (const l of (items || [])) {
      const idStr = l?.itemId ? String(l.itemId) : '';
      if (!idStr) continue;
      const qty = Number(l.quantity || 0);
      map.set(idStr, (map.get(idStr) || 0) + qty);
    }
    return map;
  }, [items]);

  const filteredCatalog = useMemo(() => {
    const q = (itemSearch || '').trim().toLowerCase();
    return (itemsCatalog || []).filter((it) => {
      if (itemType === 'service' && !it.isService) return false;
      if (itemType === 'product' && it.isService) return false;
      if (!q) return true;
      return (
        (it.name || '').toLowerCase().includes(q) ||
        (it.description || '').toLowerCase().includes(q) ||
        (it.hsnsac || '').toLowerCase().includes(q)
      );
    });
  }, [itemsCatalog, itemSearch, itemType]);

  const addOrIncItemToRecurring = (master) => {
    if (!master?.id) return;
    const idStr = String(master.id);
    const companyState = companyData?.stateCode;
    const pos = posStateCode;
    const isInter = companyState && pos && companyState !== pos;
    setItems(prev => {
      const idx = (prev || []).findIndex((l) => String(l.itemId || '') === idStr);
      if (idx >= 0) {
        return (prev || []).map((l, i) => {
          if (i !== idx) return l;
          const nextQty = Math.max(0, Number(l.quantity || 0) + 1);
          return { ...l, quantity: nextQty };
        });
      }
      const ig = (master.igstRate != null ? Number(master.igstRate) : (Number(master.cgstRate || 0) + Number(master.sgstRate || 0)));
      const cg = Number(master.cgstRate || 0);
      const sg = Number(master.sgstRate || 0);
      const taxes = isInter
        ? { cgstRate: 0, sgstRate: 0, igstRate: ig, cessRate: Number(master.cessRate ?? 0) }
        : { cgstRate: cg, sgstRate: sg, igstRate: cg + sg, cessRate: Number(master.cessRate ?? 0) };
      return [
        ...(prev || []),
        {
          id: null,
          itemId: master.id,
          name: master.name || '',
          description: master.description || '',
          quantity: 1,
          unit: master.unit || 'NOS',
          price: Number(master.price ?? 0),
          discountAmount: 0,
          discountPercent: 0,
          ...taxes
        }
      ];
    });
  };

  const decItemInRecurring = (master) => {
    if (!master?.id) return;
    const idStr = String(master.id);
    setItems(prev => {
      const arr = [...(prev || [])];
      const idx = arr.findIndex((l) => String(l.itemId || '') === idStr);
      if (idx < 0) return prev;
      const curQty = Number(arr[idx]?.quantity || 0);
      const nextQty = Math.max(0, curQty - 1);
      if (nextQty <= 0) return arr.filter((_, i) => i !== idx);
      arr[idx] = { ...arr[idx], quantity: nextQty };
      return arr;
    });
  };

  const validate = () => {
    if (!posStateCode) return 'Place of supply (state code) is required';
    if (!Array.isArray(items) || items.length === 0) return 'Add at least one item';
    for (const it of items) {
      if (!it.name?.trim()) return 'Item name is required';
      if (Number(it.quantity) <= 0) return 'Quantity must be > 0';
      if (Number(it.price) < 0) return 'Price cannot be negative';
    }
    return '';
  };

  const save = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    try {
      setSaving(true);
      await api.put(`/api/recurring-invoices/${id}`, {
        posStateCode,
        terms,
        notes,
        items: items.map(l => ({
          itemId: l.itemId || null,
          name: l.name || null,
          description: l.description || null,
          quantity: Number(l.quantity ?? 1),
          unit: l.unit || 'NOS',
          price: Number(l.price ?? 0),
          discountAmount: Number(l.discountAmount ?? 0),
          discountPercent: Number(l.discountPercent ?? 0),
          cgstRate: Number(l.cgstRate ?? 0),
          sgstRate: Number(l.sgstRate ?? 0),
          igstRate: Number(l.igstRate ?? 0),
          cessRate: Number(l.cessRate ?? 0)
        }))
      });
      setToast('Recurring invoice updated');
      navigate('/recurring-invoices');
    } catch (e) {
      setError('Failed to save recurring invoice');
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => {
    let subT = 0;
    for (const l of items) {
      const qty = Number(l.quantity || 0);
      const price = Number(l.price || 0);
      const discA = Number(l.discountAmount || 0);
      const discP = Number(l.discountPercent || 0);
      const gross = qty * price;
      const disc = discA || +((gross * Math.max(0, Math.min(100, discP))) / 100).toFixed(2);
      subT += Math.max(0, +(gross - disc).toFixed(2));
    }
    return { subTotal: +subT.toFixed(2) };
  }, [items]);

  const computed = useMemo(() => {
    const companyState = companyData?.stateCode || '';
    const pos = posStateCode || '';
    const isInter = companyState && pos && companyState !== pos;
    const noGst = !companyData?.gstin;
    let grossTotal = 0;
    let lineDiscountTotal = 0;
    let taxableValue = 0;
    let cgstAmount = 0;
    let sgstAmount = 0;
    let igstAmount = 0;
    let cessAmount = 0;
    for (const l of (items || [])) {
      const qty = Number(l.quantity || 0);
      const price = Number(l.price || 0);
      const gross = qty * price;
      const discAmt = Math.max(0, Number(l.discountAmount || 0));
      const discPct = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
      const discFromPct = +((gross * discPct) / 100).toFixed(2);
      const lineDisc = discAmt > 0 ? Math.min(gross, discAmt) : Math.min(gross, discFromPct);
      const netBeforeTax = +Math.max(0, gross - lineDisc).toFixed(2);

      const cgRate = noGst || isInter ? 0 : Number(l.cgstRate || 0);
      const sgRate = noGst || isInter ? 0 : Number(l.sgstRate || 0);
      const igRate = noGst || !isInter ? 0 : Number(l.igstRate || 0);
      const csRate = noGst ? 0 : Number(l.cessRate || 0);
      const cg = +((netBeforeTax * cgRate) / 100).toFixed(2);
      const sg = +((netBeforeTax * sgRate) / 100).toFixed(2);
      const ig = +((netBeforeTax * igRate) / 100).toFixed(2);
      const cs = +((netBeforeTax * csRate) / 100).toFixed(2);

      grossTotal += gross;
      lineDiscountTotal += lineDisc;
      taxableValue += netBeforeTax;
      cgstAmount += cg;
      sgstAmount += sg;
      igstAmount += ig;
      cessAmount += cs;
    }
    grossTotal = +grossTotal.toFixed(2);
    lineDiscountTotal = +lineDiscountTotal.toFixed(2);
    taxableValue = +taxableValue.toFixed(2);
    cgstAmount = +cgstAmount.toFixed(2);
    sgstAmount = +sgstAmount.toFixed(2);
    igstAmount = +igstAmount.toFixed(2);
    cessAmount = +cessAmount.toFixed(2);
    const total = +(
      taxableValue +
      (noGst ? 0 : (cgstAmount + sgstAmount + igstAmount + cessAmount))
    ).toFixed(2);
    return {
      grossTotal,
      lineDiscountTotal,
      docDiscount: 0,
      taxableValue,
      cgstAmount,
      sgstAmount,
      igstAmount,
      cessAmount,
      total,
      supplyType: isInter ? 'INTER' : 'INTRA',
      noGst
    };
  }, [items, companyData?.stateCode, companyData?.gstin, posStateCode]);

  // Recurring invoices don’t currently have a document-level discount field in the model,
  // but users expect to see a "Doc Discount" row like Quote/Invoice. We show it as N/A (0) for clarity.
  const docDiscountLabel = 'Doc Discount (N/A for recurring invoices)';

  return (
    <div className="page-content">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>Edit Recurring Invoice</h2>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => navigate('/recurring-invoices')}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>

      {!!error && <div style={{ marginBottom:8, color:'#991b1b', background:'#fef2f2', padding:'8px 12px', borderRadius:8 }}>{error}</div>}
      {!!toast && <div style={{ marginBottom:8, color:'#065f46', background:'#ecfdf5', padding:'8px 12px', borderRadius:8 }}>{toast}</div>}

      {loading ? (
        <div className="form-section"><div className="loading-spinner" /></div>
      ) : (
        <>
          {rec && (
            <div className="form-section">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <h3 style={{ margin:0, fontSize:16 }}>Recurring Invoice Setup</h3>
                <button className="btn btn-secondary" onClick={() => setEditSetup(v => !v)}>{editSetup ? 'Close' : 'Edit'}</button>
              </div>
              {!editSetup ? (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3, minmax(0,1fr))', gap:8, fontSize:12 }}>
                  <div><strong>Name:</strong> {rec.name}</div>
                  <div><strong>Interval:</strong> {rec.billingInterval} {Number(rec.intervalCount) > 1 ? `×${rec.intervalCount}` : ''}</div>
                  <div><strong>Anchor:</strong> {rec.billingAnchor} {rec.billingAnchor === 'DAY_OF_MONTH' && rec.dayOfMonth ? `(${rec.dayOfMonth})` : ''}</div>
                  <div><strong>Start Date:</strong> {rec.startDate}</div>
                  <div><strong>End Date:</strong> {rec.endDate || '-'}</div>
                  <div><strong>Credit Days:</strong> {rec.dueDays}</div>
                  <div><strong>Currency:</strong> {rec.currency}</div>
                </div>
              ) : (
                <div className="form-row" style={{ display:'grid', gridTemplateColumns:'repeat(3, minmax(0,1fr))', gap:8 }}>
                  <div className="form-group">
                    <label style={labelStyle}>Name</label>
                    <input className="form-control" style={controlStyle} value={rec.name || ''} onChange={(e) => setRec(prev => ({ ...prev, name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label style={labelStyle}>Interval</label>
                    <select className="form-control" style={controlStyle} value={rec.billingInterval} onChange={(e) => setRec(prev => ({ ...prev, billingInterval: e.target.value }))}>
                      <option value="MONTH">Monthly</option>
                      <option value="QUARTER">Quarterly</option>
                      <option value="YEAR">Yearly</option>
                      <option value="CUSTOM">Custom</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label style={labelStyle}>Count</label>
                    <input className="form-control" style={controlStyle} type="number" min="1" value={rec.intervalCount || 1} onChange={(e) => setRec(prev => ({ ...prev, intervalCount: Number(e.target.value || 1) }))} />
                  </div>
                  <div className="form-group">
                    <label style={labelStyle}>Anchor</label>
                    <select className="form-control" style={controlStyle} value={rec.billingAnchor} onChange={(e) => setRec(prev => ({ ...prev, billingAnchor: e.target.value }))}>
                      <option value="ANNIVERSARY">Anniversary</option>
                      <option value="DAY_OF_MONTH">Day of Month</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label style={labelStyle}>Day of Month</label>
                    <input className="form-control" style={controlStyle} type="number" min="1" max="28" value={rec.dayOfMonth || ''} disabled={rec.billingAnchor !== 'DAY_OF_MONTH'} onChange={(e) => setRec(prev => ({ ...prev, dayOfMonth: e.target.value ? Number(e.target.value) : null }))} />
                  </div>
                  <div className="form-group">
                    <label style={labelStyle}>Start Date</label>
                    <input className="form-control" style={controlStyle} type="date" value={rec.startDate || ''} onChange={(e) => setRec(prev => ({ ...prev, startDate: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label style={labelStyle}>End Date (optional)</label>
                    <input className="form-control" style={controlStyle} type="date" value={rec.endDate || ''} onChange={(e) => setRec(prev => ({ ...prev, endDate: e.target.value || null }))} />
                  </div>
                  <div className="form-group">
                    <label style={labelStyle}>Due Days</label>
                    <input className="form-control" style={controlStyle} type="number" min="0" value={rec.dueDays || 0} onChange={(e) => setRec(prev => ({ ...prev, dueDays: Number(e.target.value || 0) }))} />
                  </div>
                  <div className="form-group">
                    <label style={labelStyle}>Currency</label>
                    <select className="form-control" style={controlStyle} value={rec.currency || 'INR'} onChange={(e) => setRec(prev => ({ ...prev, currency: e.target.value }))}>
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn:'1 / -1', display:'flex', justifyContent:'flex-end', gap:8 }}>
                    <button className="btn btn-secondary" style={smallBtn} onClick={() => setEditSetup(false)}>Cancel</button>
                    <button className="btn btn-primary" onClick={async () => {
                      try {
                        setSaving(true);
                        const payload = {
                          name: rec.name,
                          billingInterval: rec.billingInterval,
                          intervalCount: rec.intervalCount,
                          billingAnchor: rec.billingAnchor,
                          dayOfMonth: rec.dayOfMonth,
                          startDate: rec.startDate,
                          endDate: rec.endDate || null,
                          dueDays: rec.dueDays,
                          currency: rec.currency
                        };
                        await api.put(`/api/recurring-invoices/${id}`, payload);
                        setToast('Setup updated');
                        setEditSetup(false);
                      } catch (_) {
                        setError('Failed to update setup');
                      } finally {
                        setSaving(false);
                      }
                    }} style={smallBtn}>Save Setup</button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="form-section" style={{ marginBottom: 0, paddingBottom: 0 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 460px', gap: 12 }}>
              {/* Left: item card picker */}
              <div style={{ border:'1px solid #e5e7eb', borderRadius: 10, padding: 12, background:'#fff' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 6 }}>
                  <h3 style={{ margin:0, fontSize:16 }}>Items</h3>
                </div>
                <div className="form-subtitle" style={{ fontSize: 12, marginBottom: 10 }}>
                  Search and add items using the cards. Use the edit icon on a card to update the item master.
                </div>

                <div style={{ marginTop: 10, border:'1px solid #e5e7eb', borderRadius: 10, padding: 10, background:'#fff' }}>
                  <div className="form-row" style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'nowrap' }}>
                    <div className="form-group" style={{ flex: 1, minWidth: 0, margin: 0 }}>
                      <div style={{ position:'relative' }}>
                        <input
                          aria-label="Search Items"
                          className="form-control"
                          style={{ ...controlStyle, paddingLeft: 30, width:'100%' }}
                          placeholder="Name / description / HSN-SAC"
                          value={itemSearch}
                          onChange={(e) => setItemSearch(e.target.value)}
                        />
                        <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#6b7280' }}><FiSearch /></span>
                      </div>
                    </div>
                    <select aria-label="Type" className="form-control" style={{ ...controlStyle, width: 140 }} value={itemType} onChange={(e) => setItemType(e.target.value)}>
                      <option value="all">All</option>
                      <option value="product">Products</option>
                      <option value="service">Services</option>
                    </select>
                    <button className="btn btn-secondary" style={smallBtn} onClick={() => setItemSearch('')}>Reset</button>
                  </div>

                  <div style={{ marginTop: 10, display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                    {filteredCatalog.slice(0, 80).map((it) => (
                      <ItemCard
                        key={it.id}
                        item={it}
                        qtyInCart={recurringQtyForItem.get(String(it.id)) || 0}
                        pulse={(recurringQtyForItem.get(String(it.id)) || 0) > 0}
                        onAdd={() => addOrIncItemToRecurring(it)}
                        onIncrement={() => addOrIncItemToRecurring(it)}
                        onDecrement={() => decItemInRecurring(it)}
                        onEdit={() => setSelectedItem({ ...it })}
                      />
                    ))}
                  </div>
                  {filteredCatalog.length > 80 && (
                    <div className="field-note" style={{ marginTop: 8, fontSize: 11 }}>
                      Showing first 80 items. Refine your search to see more.
                    </div>
                  )}
                  {!filteredCatalog.length && (
                    <div style={{ padding: 12, textAlign:'center', color:'#6b7280', fontSize: 12 }}>No items found.</div>
                  )}
                </div>
              </div>

              {/* Right: POS + summary (Quote/Invoice-style) */}
              <div style={{ border:'1px solid #e5e7eb', borderRadius: 10, padding: 12, background:'#fff', position:'sticky', top: 12, alignSelf:'start', maxHeight:'calc(100vh - 24px)', overflowY:'auto' }}>
                <div className="form-group" style={{ marginBottom: 8 }}>
                  <label style={labelStyle}>POS (State)</label>
                  <select className="form-control" style={controlStyle} value={posStateCode} onChange={(e) => setPosStateCode(e.target.value)}>
                  <option value="">Select State</option>
                  {INDIAN_STATES.map(s => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
                </select>
                  <div className="field-note" style={{ fontSize: 11 }}>POS affects default tax fields when selecting catalog items.</div>
                </div>

                <div style={{ fontWeight: 700, fontSize: 14 }}>Details</div>
                <div className="field-note" style={{ fontSize: 11, marginTop: 6 }}>
                  <div>Company GSTIN: {companyData?.gstin || '-'} | State Code: {companyData?.stateCode || '-'}</div>
                  <div>Customer: {customerData?.name || (rec?.customerId ? `#${rec.customerId}` : '-')}</div>
                  <div>Customer GSTIN: {customerData?.gstin || '-'} | State Code: {customerData?.stateCode || '-'}</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 8, marginTop: 6 }}>
                    <div>Email: {customerData?.email || '-'}</div>
                    <div>Phone: {customerData?.phone || '-'}</div>
                  </div>
                </div>

                <div style={{ borderTop:'1px dashed #e5e7eb', margin:'10px 0' }} />

                <div style={{ fontWeight: 700, fontSize: 14 }}>Totals</div>
                <div className="field-note" style={{ fontSize: 11 }}>
                  Supply Type: {computed.supplyType === 'INTER' ? 'Interstate (IGST)' : 'Intrastate (CGST + SGST)'}{computed.noGst ? ' - GST disabled (no company GSTIN)' : ''}
                </div>

                <div style={{ borderTop:'1px dashed #e5e7eb', margin:'10px 0' }} />

                <div style={{ fontWeight: 700, fontSize: 14 }}>Summary</div>
                <div className="field-note" style={{ fontSize: 11 }}>
                  Line discount preview uses: amount if set, otherwise percent.
                </div>

                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.6fr 0.9fr 0.9fr 72px', gap: 6, fontSize: 12, border:'1px solid #e5e7eb', borderRadius:8, padding:8, background:'#fafafa' }}>
                    <div style={{ fontWeight: 600, color:'#6b7280' }}>Item</div>
                    <div style={{ fontWeight: 600, color:'#6b7280' }}>Qty</div>
                    <div style={{ fontWeight: 600, textAlign: 'right', color:'#6b7280' }}>Disc</div>
                    <div style={{ fontWeight: 600, textAlign: 'right', color:'#6b7280' }}>Net</div>
                    <div style={{ fontWeight: 600, textAlign: 'right', color:'#6b7280' }}>Edit</div>

                    {(items || []).map((l, idx) => {
                      if (!l || Number(l.quantity || 0) <= 0) return null;
                      const qty = Number(l.quantity || 0);
                      const price = Number(l.price || 0);
                      const gross = qty * price;
                      const discAmt = Math.max(0, Number(l.discountAmount || 0));
                      const discPct = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
                      const discFromPct = +((gross * discPct) / 100).toFixed(2);
                      const effectiveDisc = discAmt > 0 ? discAmt : discFromPct;
                      const net = +Math.max(0, gross - effectiveDisc).toFixed(2);
                      const name = l.name || itemsCatalog.find(x => String(x.id) === String(l.itemId))?.name || 'Item';
                      const discKey = String(idx);
                      const mode = (discAmt > 0) ? 'amount' : 'percent';
                      const value = mode === 'amount' ? (l.discountAmount ?? '') : (l.discountPercent ?? '');
                      return (
                        <React.Fragment key={`${discKey}-${String(l.itemId || '')}`}>
                          <div title={name} style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{name}</div>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            className="form-control"
                            style={{ ...controlStyle, width: '100%', textAlign: 'right', padding: '2px 6px', height: 28, minHeight: 28 }}
                            value={qty}
                            onChange={(e) => {
                              const next = Math.max(0, parseInt(e.target.value || '0', 10));
                              if (next <= 0) {
                                removeItem(idx);
                                return;
                              }
                              updateItem(idx, 'quantity', next);
                            }}
                            aria-label={`Quantity for ${name}`}
                          />
                          <div style={{ textAlign:'right' }}>{+effectiveDisc.toFixed(2)}</div>
                          <div style={{ textAlign:'right', fontWeight: 600 }}>{net}</div>
                          <div style={{ textAlign:'right' }}>
                            <button
                              className="btn btn-secondary"
                              style={{ fontSize: 11, padding:'4px 8px', height: 28, minHeight: 28, borderRadius: 8 }}
                              onClick={() => setOpenDisc((m) => ({ ...m, [discKey]: !m[discKey] }))}
                              title="Edit discount"
                              type="button"
                            >
                              + Disc
                            </button>
                          </div>
                          {!!openDisc[discKey] && (
                            <div style={{ gridColumn: '1 / -1', background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, padding:8 }}>
                              <div style={{ display:'grid', gridTemplateColumns:'1fr 110px 110px', gap:8, alignItems:'center' }}>
                                <div style={{ fontSize: 11, color:'#6b7280' }}>Discount for {name}</div>
                                <input
                                  className="form-control"
                                  style={{ ...controlStyle, width:'100%', textAlign:'right' }}
                                  type="number"
                                  min="0"
                                  step={mode === 'percent' ? '1' : '0.01'}
                                  value={value}
                                  onChange={(e) => {
                                    const v = clamp(e.target.value);
                                    if (mode === 'percent') updateItem(idx, 'discountPercent', v === '' ? '' : Number(v));
                                    else updateItem(idx, 'discountAmount', v === '' ? '' : Number(v));
                                  }}
                                />
                                <select
                                  className="form-control"
                                  style={{ ...controlStyle, width:'100%' }}
                                  value={mode}
                                  onChange={(e) => {
                                    // UI only: switching mode doesn't overwrite the other field (keeps existing recurring logic intact)
                                    const next = e.target.value;
                                    if (next === 'percent' && !l.discountPercent) updateItem(idx, 'discountPercent', 0);
                                    if (next === 'amount' && !l.discountAmount) updateItem(idx, 'discountAmount', 0);
                                  }}
                                  aria-label="Discount type"
                                >
                                  <option value="amount">₹</option>
                                  <option value="percent">% </option>
                                </select>
                              </div>
                              <div className="field-note" style={{ fontSize: 11, marginTop: 6 }}>
                                Current values: ₹{Number(l.discountAmount || 0)} and {Number(l.discountPercent || 0)}%
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {(!items || items.length === 0) && (
                      <div style={{ gridColumn:'1 / -1', padding:'8px 2px', color:'#6b7280' }}>No items yet. Add from the cards.</div>
                    )}
                  </div>
                </div>

                <div style={{ borderTop:'1px dashed #e5e7eb', margin:'10px 0' }} />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 6, fontSize: 12 }}>
                  <div style={{ color:'#6b7280' }}>Total</div><div style={{ textAlign:'right', fontWeight: 600 }}>{computed.grossTotal}</div>
                  <div style={{ color:'#6b7280' }}>Item Discounts</div><div style={{ textAlign:'right' }}>{computed.lineDiscountTotal}</div>
                  <div style={{ color:'#6b7280' }}>{docDiscountLabel}</div><div style={{ textAlign:'right' }}>{computed.docDiscount}</div>
                  <div style={{ color:'#6b7280' }}>Taxable Value</div><div style={{ textAlign:'right' }}>{computed.taxableValue}</div>
                  <div style={{ color:'#6b7280' }}>CGST</div><div style={{ textAlign:'right' }}>{computed.noGst ? 0 : computed.cgstAmount}</div>
                  <div style={{ color:'#6b7280' }}>SGST</div><div style={{ textAlign:'right' }}>{computed.noGst ? 0 : computed.sgstAmount}</div>
                  <div style={{ color:'#6b7280' }}>IGST</div><div style={{ textAlign:'right' }}>{computed.noGst ? 0 : computed.igstAmount}</div>
                  <div style={{ color:'#6b7280' }}>Cess</div><div style={{ textAlign:'right' }}>{computed.noGst ? 0 : computed.cessAmount}</div>
                  <div style={{ fontWeight: 700 }}>Grand Total</div><div style={{ textAlign:'right', fontWeight: 700 }}>{computed.total}</div>
              </div>
              </div>
            </div>
          </div>

          {/* Item master edit modal */}
          {!!selectedItem && (
            <EditItemModal
              item={selectedItem}
              onChange={(field, value) => setSelectedItem((s) => ({ ...(s || {}), [field]: value }))}
              onClose={() => setSelectedItem(null)}
              onSave={async (payload) => {
                try {
                  if (!payload?.id) return;
                  await ItemsAPI.update(payload.id, { ...payload, companyId: company?.id });
                  await refreshItemsCatalog();
                  setSelectedItem(null);
                } catch (e) {
                  console.error('Save item failed', e);
                }
              }}
            />
                )}

          <div className="form-section">
            <div className="form-row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div className="form-group">
                <label>Terms</label>
                <textarea className="form-control" rows={5} value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Terms and conditions visible to customer" />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea className="form-control" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes (optional)" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}







